import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { colors, themePalette } from 'app//components/ThemeProvider/themeColors';
import ModalX from 'app/shared/ui/elements/Modal-X';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStatusConfig } from './model/constans';

export interface EmployeeStatus {
  customer_name: string;
  card_transfer_status: string;
}

interface StatusesModalProps {
  open: boolean;
  onClose: () => void;
  employees?: EmployeeStatus[];
  isLoading: boolean;
}

const StatusesModal = ({ open, onClose, employees, isLoading }: StatusesModalProps) => {
  const { t } = useTranslation('salary-card');
  const statusConfig = getStatusConfig(t);
  const [filter, setFilter] = useState<string>('all');

  const filtered =
    filter === 'all' ? employees : employees?.filter(e => e.card_transfer_status === filter);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: { xs: 'calc(100% - 24px)', sm: '560px', md: '620px' },
          maxWidth: '620px',
          height: { xs: 'auto', sm: 'min(720px, calc(100dvh - 64px))' },
          maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 64px)' },
          m: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 4 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <ModalX close={onClose} />

        <Typography fontSize={20} fontWeight={600} lineHeight='28px'>
          {t('statuses.modalTitle')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mt: 3,
            mb: 2,
          }}
        >
          <Typography fontSize={14} color={themePalette.text_and_icon.third_party}>
            {t('statuses.employeeCount', { count: filtered?.length ?? 0 })}
          </Typography>

          <Select
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            size='small'
          >
            <MenuItem value={'all'}>{t('statuses.all')}</MenuItem>
            {Object.values(statusConfig).map(status => (
              <MenuItem key={status.label} value={status.label}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : filtered?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flex: 1,
              py: 6,
            }}
          >
            <Typography fontSize={16}>{t('statuses.emptyByFilter')}</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowY: 'auto', flex: 1, pr: 1 }}>
            {filtered?.map((employee, index) => {
              const config = statusConfig[employee?.card_transfer_status];

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    py: 2,
                    borderBottom:
                      index < filtered.length - 1 ? `1px solid ${colors.base[100]}` : 'none',
                  }}
                >
                  <Typography fontSize={14} fontWeight={500}>
                    {employee.customer_name}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '6px',
                      backgroundColor: config.backgroundColor,
                      alignSelf: { xs: 'flex-start', sm: 'auto' },
                    }}
                  >
                    {config.icon}
                    <Typography fontSize={13} fontWeight={500} color={config.color}>
                      {config.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatusesModal;
